/**
 * Login del staff (contrato §1, RN-ROL-16/17).
 *
 * Dos pasos posibles: credenciales y, para los roles con acceso a facturación total,
 * **código de 6 dígitos al email** (RN-ROL-17). El bloqueo por 5 intentos fallidos lo
 * decide el backend (RN-ROL-16) y llega como error del contrato: se muestra el mensaje
 * tal cual. El código de regla (`error.rule`) NO se dibuja: es para el desarrollador,
 * no para quien está entrando al sistema.
 */
import { useState, type FormEvent } from "react";
import { Navigate, useLocation, useNavigate } from "react-router";
import { KeyRound, Mail, ShieldCheck } from "lucide-react";
import { ApiError } from "../../lib/api.js";
import { useAuth } from "../../lib/auth/index.js";
import { landingFor } from "../../app/nav.js";
import { Button } from "../../components/ui/Button.js";
import { Input } from "../../components/ui/Input.js";
import { AuthShell } from "./AuthShell.js";

type Step = { kind: "credentials" } | { kind: "twoFactor"; challengeId: string; email: string };

export function LoginPage() {
  const { status, user, login, verifyTwoFactor, signedOutReason } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();

  const [step, setStep] = useState<Step>({ kind: "credentials" });
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [code, setCode] = useState("");
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  if (status === "authenticated" && user) {
    const from = (location.state as { from?: string } | null)?.from;
    return <Navigate to={from ?? landingFor(user.role)} replace />;
  }

  function handleError(cause: unknown) {
    if (cause instanceof ApiError) {
      setError(cause.message);
      return;
    }
    setError("No se pudo iniciar sesión. Probá de nuevo.");
  }

  async function submitCredentials(event: FormEvent) {
    event.preventDefault();
    setLoading(true);
    setError(null);
    try {
      const result = await login(email.trim(), password);
      if ("twoFactorRequired" in result) {
        setStep({ kind: "twoFactor", challengeId: result.challengeId, email: email.trim() });
        setLoading(false);
        return;
      }
      navigate("/", { replace: true });
    } catch (cause) {
      handleError(cause);
      setLoading(false);
    }
  }

  async function submitCode(event: FormEvent) {
    event.preventDefault();
    if (step.kind !== "twoFactor") return;
    setLoading(true);
    setError(null);
    try {
      await verifyTwoFactor(step.challengeId, code);
      navigate("/", { replace: true });
    } catch (cause) {
      handleError(cause);
      setLoading(false);
    }
  }

  if (step.kind === "twoFactor") {
    return (
      <AuthShell
        kicker="Verificación en dos pasos"
        title="Escribí el código"
        description={
          <>
            Mandamos un código de 6 dígitos a <strong className="text-ink">{step.email}</strong>.
            Vence en 5 minutos y sirve una sola vez.
          </>
        }
        footer="Los roles que ven la facturación total entran con segundo factor."
      >
        <form onSubmit={(event) => void submitCode(event)} className="flex flex-col gap-4">
          <Input
            label="Código"
            inputMode="numeric"
            autoComplete="one-time-code"
            maxLength={6}
            autoFocus
            icon={<ShieldCheck />}
            value={code}
            onChange={(event) => setCode(event.target.value.replace(/\D/g, ""))}
            placeholder="000000"
            className="text-center font-mono text-[18px] tracking-[0.5em]"
          />
          {error ? (
            <p role="alert" className="text-[12px] text-danger-fg">
              {error}
            </p>
          ) : null}
          <Button
            type="submit"
            variant="primary"
            block
            loading={loading}
            disabled={code.length !== 6}
          >
            Entrar
          </Button>
          <Button
            variant="ghost"
            block
            onClick={() => {
              setStep({ kind: "credentials" });
              setCode("");
              setError(null);
            }}
          >
            Volver
          </Button>
        </form>
      </AuthShell>
    );
  }

  return (
    <AuthShell
      kicker="Acceso del personal"
      title="Entrá al sistema"
      description="Portal admin, portal profesores y tablet kiosko. Todo lo que hagas queda registrado con tu usuario y con la sede que elijas."
      footer={
        <a href="/recuperar" className="transition-colors hover:text-ink">
          Olvidé mi contraseña
        </a>
      }
    >
      <form onSubmit={(event) => void submitCredentials(event)} className="flex flex-col gap-4">
        {signedOutReason ? (
          <p className="rounded-[10px] border border-warning/35 bg-warning/10 px-3 py-2 text-[12px] leading-snug text-ink">
            {signedOutReason}
          </p>
        ) : null}

        <Input
          label="Email"
          type="email"
          autoComplete="username"
          required
          autoFocus
          icon={<Mail />}
          value={email}
          onChange={(event) => setEmail(event.target.value)}
          placeholder="vos@viva.local"
        />
        <Input
          label="Contraseña"
          type="password"
          autoComplete="current-password"
          required
          icon={<KeyRound />}
          value={password}
          onChange={(event) => setPassword(event.target.value)}
          placeholder="••••••••"
        />

        {error ? (
          <p role="alert" className="text-[12px] text-danger-fg">
            {error}
          </p>
        ) : null}

        <Button type="submit" variant="primary" block loading={loading}>
          Iniciar sesión
        </Button>
      </form>
    </AuthShell>
  );
}

export default LoginPage;
